import {reduce} from 'axax/esnext/reduce'
import * as t from 'io-ts'
import chunk from 'lodash.chunk'
import zip from 'lodash.zip'
import api, {ApiInstance} from '../http/api'
import {Playlist, PlaylistCodec} from '../types'
import {validate} from '../types/validate'
import {sortSimilar} from './similarity'

export type MergeSort =
  | undefined
  | 'added'
  | 'danceability'
  | 'energy'
  | 'valence'
  | 'tempo'
  | 'similarity'

export type MergeDirection = 'asc' | 'desc'

const isNode =
  typeof process !== 'undefined' &&
  process.versions != null &&
  process.versions.node != null

// peaks on audio features, lets go two or whatever

const AudioFeaturesCodec = t.type({
  danceability: t.number,
  energy: t.number,
  key: t.number,
  loudness: t.number,
  mode: t.number,
  speechiness: t.number,
  acousticness: t.number,
  instrumentalness: t.number,
  liveness: t.number,
  valence: t.number,
  tempo: t.number,
  type: t.string,
  id: t.string,
  uri: t.string,
  track_href: t.string,
  analysis_url: t.string,
  duration_ms: t.number,
  time_signature: t.number
})

export type AudioFeatures = t.TypeOf<typeof AudioFeaturesCodec>

const AudioFeaturesResponseCodec = t.type({
  audio_features: t.array(AudioFeaturesCodec)
})

const TrackCodec = t.intersection([
  t.type({id: t.string}),
  t.partial({name: t.string})
])

// LocalTracks are not playable
const LocalTrack = t.type({id: t.null, is_local: t.literal(true)})

const PlaylistItemCodec = t.type({
  added_at: t.string,
  track: t.union([TrackCodec, LocalTrack])
})

const PlayablePlaylistItemCodec = t.type({
  added_at: t.string,
  track: TrackCodec
})

type PlayablePlaylistItem = t.TypeOf<typeof PlayablePlaylistItemCodec>

type PlaylistItem = t.TypeOf<typeof PlaylistItemCodec>

const PlaylistTracksCodec = t.type({
  items: t.array(PlaylistItemCodec),
  total: t.number
})

type PlaylistTracks = t.TypeOf<typeof PlaylistTracksCodec>

const PlaylistWithTracksCodec = t.intersection([
  PlaylistCodec,
  t.type({
    tracks: PlaylistTracksCodec
  })
])

type PlaylistWithTracks = t.TypeOf<typeof PlaylistWithTracksCodec>

const UserCodec = t.intersection([
  t.type({
    id: t.string,
    type: t.literal('user')
  }),
  t.partial({
    country: t.string,
    product: t.string,
    images: t.array(t.type({url: t.string}))
  })
])

const CurrentlyPlayingCodec = t.union([
  t.undefined,
  t.type({
    item: t.type({
      id: t.string,
      album: t.type({
        id: t.string,
        images: t.array(t.type({url: t.string, height: t.number}))
      })
    })
  })
])

const PlaylistsCodec = t.type({
  items: t.array(PlaylistCodec),
  total: t.number,
  limit: t.number,
  offset: t.number,
  next: t.union([t.string, t.null]),
  previous: t.union([t.string, t.null])
})

type GetPlaylist = {
  (id: string): Promise<Playlist>
  (id: string, withTracks: false): Promise<Playlist>
  (id: string, withTracks: true): Promise<PlaylistWithTracks>
}

export type User = t.TypeOf<typeof UserCodec>
export type AuthState =
  | {authenticated: false}
  | {authenticated: true; user: User}

export const init = (
  getToken: () => Promise<string | undefined> | string | undefined,
  refreshToken?: () => Promise<void>
) => {
  const spotifyApi = api('https://api.spotify.com/v1/', getToken, refreshToken)

  // spotify gives errors sometimes when hit quickly which are resolved by retrying
  const spotifyApiWithRetry =
    (maxRetry: number, retry = 0): ApiInstance =>
    async (...args) => {
      const res = await spotifyApi(...args)
      if (!res.ok) {
        console.log(
          `Status ${res.status}; Retry ${retry} for ${args[0]} ${
            args[1]
          } ${JSON.stringify(res.response.headers, null, 2)}`
        )
        if (res.status === 429) {
          await new Promise(resolve => setTimeout(resolve, 1000 * 40))
        }
        if ((res.status === 404 || res.status === 502) && retry < maxRetry) {
          return spotifyApiWithRetry(maxRetry, retry + 1)(...args)
        }
      }
      return res
    }
  const spotify = spotifyApiWithRetry(3)
  const authState: Map<string, Promise<AuthState>> = new Map()

  const getPlaylistTracks = async (id: string, offset = 0, limit = 100) => {
    const resp = await spotify('get', `playlists/${id}/tracks`, {
      fields: 'items(added_at, track(id, name)),total',
      market: isNode ? 'us' : 'from_token',
      offset,
      limit
    })

    const payload = validate(resp.json, PlaylistTracksCodec)
    if (!payload.valid) {
      throw new Error(
        `Unable to get playlist ${id} offset ${offset} ${payload.errors.join(
          ', '
        )}`
      )
    }
    return payload.data
  }

  const getPlaylist: GetPlaylist = async (
    id: string,
    withTracks = false
  ): Promise<any> => {
    const res = await spotify('get', `playlists/${id}`, {
      fields: `id,name,snapshot_id${
        withTracks ? ',tracks(items(added_at,track(id)),total)' : ''
      }`
    })
    if (!res.ok) {
      throw new Error(`Request failed ${res.body}`)
    }
    const data = withTracks
      ? validate(res.json, PlaylistWithTracksCodec)
      : validate(res.json, PlaylistCodec)
    if (!data.valid) {
      throw new Error(data.errors.join(', '))
    }
    return data.data
  }

  const getAllPlaylistTracks = (id: string) => {
    return reduce<PlaylistTracks['items'], PlaylistTracks['items']>(
      (all, tracks) => all.concat(tracks),
      []
    )(getPlaylistIterable(id)).then(removeUnplayableTracks)
  }

  const getAudioFeatures = async (trackIds: string[]) => {
    const tracksPerRequest = 100
    const batches = [
      ...Array(Math.ceil(trackIds.length / tracksPerRequest))
    ].map(_ => trackIds.splice(0, tracksPerRequest))
    const features = await Promise.all(
      batches.map(ids =>
        spotify('get', 'audio-features', {ids}).then(res => {
          if (res.ok) {
            const validated = validate(res.json, AudioFeaturesResponseCodec)
            if (validated.valid) {
              return validated.data.audio_features
            }
          }
          console.warn('Unable to fetch audio-features for tracks', ids)
          return []
        })
      )
    )
    const allFeatures = features.reduce((all, list) => all.concat(list), [])
    const groupedByTrack = allFeatures.reduce(
      (grouped: Record<string, AudioFeatures>, features) => ({
        ...grouped,
        [features.id]: features
      }),
      {}
    )
    return groupedByTrack
  }

  const getPlaylistIterable = (id: string, offset = 0) => {
    let done = false
    return {
      [Symbol.asyncIterator]() {
        return {
          offset,
          async next(): Promise<
            | {
                value: PlaylistTracks['items']
                done: false
              }
            | {
                value: undefined
                done: true
              }
          > {
            if (done) {
              return {value: undefined, done: true}
            }

            const {total, items} = await getPlaylistTracks(id, this.offset)

            this.offset += items.length
            // console.log(`${this.offset} += ${items.length}`)
            done = this.offset >= total
            return {value: items, done: false}
          }
        }
      }
    }
  }

  const removeUnplayableTracks = (items: PlaylistItem[]) => {
    return items.filter(
      (item): item is PlayablePlaylistItem => item.track.id !== null
    )
  }

  const getPlaylistLastUpdated = async (id: string) => {
    const tracks = await getAllPlaylistTracks(id)
    return tracks.reduce(
      (max: string, item) =>
        !max || item.added_at > max ? item.added_at : max,
      '2000-01-01T00:00:00'
    )
  }

  const sortPlaylists = async (
    playlists: {id: string; snapshot_id?: string | null}[]
  ) => {
    const changed = (
      await Promise.all(
        playlists.map(async ({id, snapshot_id}) => {
          if (!snapshot_id) {
            console.warn(`Missing snapshot id for playlist ${id}`)
          }
          try {
            const current = await getPlaylist(id)
            if (snapshot_id && current.snapshot_id === snapshot_id) {
              return undefined
            }
            return current
          } catch (e) {
            console.warn('unable to get playlist', id, e)
            return undefined
          }
        })
      )
    ).filter((playlist): playlist is Playlist => playlist !== undefined)

    return (
      await Promise.all(
        changed.map(async playlist => {
          try {
            return {
              ...playlist,
              last_updated: await getPlaylistLastUpdated(playlist.id)
            }
          } catch (e) {
            console.warn('unable to get playlist last updated', playlist.id, e)
            return playlist
          }
        })
      )
    )
      .filter(
        (playlist): playlist is Playlist & {last_updated: string} =>
          'last_updated' in playlist && !!playlist.last_updated
      )
      .sort((a, b) => {
        if (a.last_updated < b.last_updated) {
          return 1
        }
        if (a.last_updated > b.last_updated) {
          return -1
        }
        return 0
      })
  }

  const sortTracks = (
    playlistItems: PlaylistItem[],
    sort: MergeSort,
    direction: MergeDirection,
    audioFeatures: Record<string, AudioFeatures>
  ) => {
    const sortDescending = direction === 'desc'
    if (!sort) {
      return playlistItems
    }

    const items = removeUnplayableTracks(playlistItems)

    if (sort === 'added') {
      return items.sort((a, b) => {
        if (a.added_at < b.added_at) {
          return sortDescending ? 1 : -1
        }
        if (a.added_at > b.added_at) {
          return sortDescending ? -1 : 1
        }
        return 0
      })
    }

    const withAudioFeatures = items.map(item => ({
      ...item,
      features:
        item.track.id in audioFeatures
          ? audioFeatures[item.track.id]
          : undefined
    }))

    if (sort === 'similarity') {
      return withAudioFeatures.sort((a, b) => {
        if (a.features && b.features) {
          return sortSimilar(a.features, b.features)
        }
        return 0
      })
    }

    return withAudioFeatures.sort((a, b) => {
      const valueA = a.features?.[sort] || 0
      const valueB = b.features?.[sort] || 0

      if (valueA < valueB) {
        return sortDescending ? 1 : -1
      }
      if (valueA > valueB) {
        return sortDescending ? -1 : 1
      }
      return 0
    })
  }

  const mergePlaylists = async (
    playlists: Omit<Playlist, 'name'>[] | string[],
    intoId: string,
    creating: boolean,
    sortDetail: [MergeSort, MergeDirection] = ['added', 'asc'],
    replacing: boolean,
    interleave: boolean
  ) => {
    const allTracksPromise = creating
      ? Promise.resolve([])
      : getAllPlaylistTracks(intoId)
    const [sort, sortDirection] = sortDetail

    const playlistsPromise = Promise.all(
      playlists.map(async playlist => {
        if (typeof playlist === 'string') {
          playlist = {id: playlist}
        }
        if (!creating && playlist.snapshot_id) {
          const remotePlaylist = await getPlaylist(playlist.id)
          if (
            !replacing &&
            remotePlaylist.snapshot_id &&
            remotePlaylist.snapshot_id === playlist.snapshot_id
          ) {
            //nothing has changed, don't bother looking at tracks
            console.log(`skipping playlist ${playlist.id}`)
            return false
          }
        }
        const all = await getAllPlaylistTracks(playlist.id)
        return all
      })
    ).then(playlists => {
      // remove skipped playlists
      return playlists.filter((r): r is PlayablePlaylistItem[] => r !== false)
    })

    const [allTracks, changedPlaylists] = await Promise.all([
      allTracksPromise,
      playlistsPromise
    ])

    const currentTrackIds = allTracks.map(item => item.track.id)

    const audioFeatures: Record<string, AudioFeatures> =
      sort && (sort in AudioFeaturesCodec.props || sort === 'similarity')
        ? await getAudioFeatures(currentTrackIds)
        : {}

    const newTracks = changedPlaylists.map(items =>
      replacing
        ? items
        : items.filter(item => !currentTrackIds.includes(item.track.id))
    )

    // [[1,2,3,4], [a,b,c,d]]
    const mergedAndSorted = interleave
      ? // [[1,a], [2,b], [3,c], [4,c]]
        // [1,a,2,b,3,c,4,d]
        zip(
          ...newTracks.map(playlist =>
            sortTracks(playlist, sort, sortDirection, audioFeatures)
          )
        ).reduce((all, tracks) => all.concat(tracks), [])
      : // [1,2,3,4, a,b,c,d]
        sortTracks(
          newTracks.reduce((all, tracks) => all.concat(tracks), []),
          sort,
          sortDirection,
          audioFeatures
        )

    const mergedIds = mergedAndSorted
      .filter((i): i is PlaylistTracks['items'][0] => !!i)
      .map(item => {
        console.log(item)
        return `spotify:track:${item.track.id}`
      })

    const unique = [...new Set(mergedIds)]

    const chunked = chunk(unique, 100)

    let position = 0
    for (let uris of chunked) {
      const method = replacing && position === 0 ? 'put' : 'post'
      await spotify(
        method,
        `playlists/${intoId}/tracks`,
        creating || method === 'put'
          ? {uris}
          : {
              uris,
              position
            }
      )
      position += uris.length - 1
    }
  }

  const createPlaylist = async (
    userId = 'me',
    name: string,
    description?: string
  ) => {
    if (userId === 'me') {
      userId = (await getMe()).id
    }
    const resp = await spotify('post', `users/${userId}/playlists`, {
      name,
      description,
      public: true
    })
    const type = validate(resp.json, PlaylistCodec)
    if (!type.valid) {
      throw new Error(type.errors.join(', '))
    }
    return type.data
  }

  const getMe = async () => {
    const res = await spotify('get', 'me')
    if (res.ok) {
      const type = validate(res.json, UserCodec)
      if (type.valid) {
        return type.data
      }
      throw new Error(type.errors.join(', '))
    }
    throw new Error(res.body)
  }

  const getAuthState = async (): Promise<AuthState> => {
    const token = await getToken()

    if (!token) {
      return {authenticated: false}
    }

    let auth = authState.get(token)

    if (auth) {
      return auth
    }

    authState.set(
      token,
      (auth = new Promise(resolve => {
        getMe()
          .then(user => {
            resolve({authenticated: true, user})
          })
          .catch(e => {
            console.debug(e)
            resolve({authenticated: false})
          })
      }))
    )

    return auth
  }

  const getCurrentlyPlaying = async () => {
    if (getToken()) {
      const res = await spotify('get', 'me/player/currently-playing')
      if (res.ok) {
        const type = validate(res.json, CurrentlyPlayingCodec)
        if (type.valid) {
          return type.data
        }
        throw new Error(type.errors.join(', '))
      }
      throw new Error(res.body)
    }
  }

  const getUsersPlaylists = async (userId: string) => {
    const res = await spotify('get', `users/${userId}/playlists`)
    if (res.ok) {
      const type = validate(res.json, PlaylistsCodec)
      if (type.valid) {
        return type.data
      }
      throw new Error(type.errors.join(', '))
    }
    throw new Error(res.body)
  }

  return {
    createPlaylist,
    getAuthState,
    getMe,
    getPlaylist,
    mergePlaylists,
    sortPlaylists,
    getCurrentlyPlaying,
    getUsersPlaylists,
    getPlaylistLastUpdated,
    api: spotify
  }
}
